import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { ChevronRight } from 'lucide-react';
import { SlashCommandItem } from './slashCommandItems';
import { cn } from '@/lib/utils';

export interface SlashCommandListProps {
  items: SlashCommandItem[];
  command: (item: SlashCommandItem) => void;
}

export interface SlashCommandListRef {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

// Ordem preferida das categorias (as demais aparecem depois em ordem alfabética).
const CATEGORY_ORDER = [
  'cliente',
  'conjuge',
  'veiculo',
  'venda',
  'compra',
  'consignacao',
  'reserva',
  'pagamento',
  'troca',
  'vendedor',
  'loja',
  'rede',
  'data',
  'garantia',
  'testemunha',
  'sistema',
  'manual',
  'texto_dinamico',
];

const SlashCommandList = forwardRef<SlashCommandListRef, SlashCommandListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    // Agrupa e ordena categorias.
    const groupedEntries = useMemo(() => {
      const groups: Record<string, { label: string; items: SlashCommandItem[] }> = {};
      for (const item of items) {
        const cat = item.category || 'manual';
        if (!groups[cat]) {
          groups[cat] = { label: item.categoryLabel || cat, items: [] };
        }
        groups[cat].items.push(item);
      }
      const keys = Object.keys(groups).sort((a, b) => {
        const ia = CATEGORY_ORDER.indexOf(a);
        const ib = CATEGORY_ORDER.indexOf(b);
        if (ia === -1 && ib === -1) return groups[a].label.localeCompare(groups[b].label);
        if (ia === -1) return 1;
        if (ib === -1) return -1;
        return ia - ib;
      });
      return keys.map((k) => [k, groups[k]] as const);
    }, [items]);

    // Estado do acordeão: começa com a primeira categoria aberta.
    const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());

    useEffect(() => {
      setSelectedIndex(0);
      // Se houver poucos itens (busca ativa), abre todas para facilitar a leitura.
      if (items.length > 0 && items.length <= 20) {
        setOpenCategories(new Set(groupedEntries.map(([k]) => k)));
      } else if (groupedEntries.length > 0) {
        setOpenCategories((prev) => {
          if (prev.size > 0) return prev;
          return new Set([groupedEntries[0][0]]);
        });
      }
    }, [items, groupedEntries]);

    const toggleCategory = useCallback((cat: string) => {
      setOpenCategories((prev) => {
        const next = new Set(prev);
        if (next.has(cat)) next.delete(cat);
        else next.add(cat);
        return next;
      });
    }, []);

    // Lista linear apenas com itens visíveis (categorias abertas) para navegação por teclado.
    const visibleItems = useMemo(() => {
      const list: SlashCommandItem[] = [];
      for (const [cat, group] of groupedEntries) {
        if (openCategories.has(cat)) list.push(...group.items);
      }
      return list;
    }, [groupedEntries, openCategories]);

    const selectItem = useCallback(
      (index: number) => {
        const item = visibleItems[index];
        if (item) command(item);
      },
      [visibleItems, command]
    );

    const upHandler = useCallback(() => {
      if (visibleItems.length === 0) return;
      setSelectedIndex((prev) => (prev - 1 + visibleItems.length) % visibleItems.length);
    }, [visibleItems.length]);

    const downHandler = useCallback(() => {
      if (visibleItems.length === 0) return;
      setSelectedIndex((prev) => (prev + 1) % visibleItems.length);
    }, [visibleItems.length]);

    const enterHandler = useCallback(() => {
      selectItem(selectedIndex);
    }, [selectItem, selectedIndex]);

    useImperativeHandle(
      ref,
      () => ({
        onKeyDown: (event: KeyboardEvent) => {
          if (event.key === 'ArrowUp') {
            upHandler();
            return true;
          }
          if (event.key === 'ArrowDown') {
            downHandler();
            return true;
          }
          if (event.key === 'Enter') {
            enterHandler();
            return true;
          }
          return false;
        },
      }),
      [upHandler, downHandler, enterHandler]
    );

    if (items.length === 0) {
      return (
        <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm text-muted-foreground">
          Nenhum campo encontrado
        </div>
      );
    }

    let flatIndex = 0;

    return (
      <div className="bg-popover border rounded-lg shadow-lg overflow-hidden min-w-[300px] max-w-[360px]">
        <div className="p-2 border-b bg-muted/30 flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">Campos Dinâmicos</span>
          <span className="text-[10px] text-muted-foreground">{items.length} campos</span>
        </div>
        <div className="max-h-[360px] overflow-y-auto p-1">
          {groupedEntries.map(([category, group]) => {
            const isOpen = openCategories.has(category);
            return (
              <div key={category} className="mb-0.5">
                <button
                  type="button"
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:bg-accent/50 rounded-md transition-colors"
                >
                  <ChevronRight
                    className={cn(
                      'h-3 w-3 transition-transform shrink-0',
                      isOpen && 'rotate-90'
                    )}
                  />
                  <span className="flex-1 text-left truncate">{group.label}</span>
                  <span className="text-[10px] normal-case font-normal text-muted-foreground/70">
                    {group.items.length}
                  </span>
                </button>
                {isOpen && (
                  <div className="ml-1">
                    {group.items.map((item) => {
                      const currentIndex = flatIndex++;
                      const isSelected = currentIndex === selectedIndex;
                      return (
                        <button
                          key={`${category}-${item.key}`}
                          className={cn(
                            'w-full text-left px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2',
                            'hover:bg-accent hover:text-accent-foreground',
                            'focus:outline-none focus:bg-accent focus:text-accent-foreground',
                            isSelected && 'bg-accent text-accent-foreground'
                          )}
                          onClick={() => selectItem(currentIndex)}
                          onMouseEnter={() => setSelectedIndex(currentIndex)}
                        >
                          <span className="font-medium truncate flex-1">{item.label}</span>
                          <span className="text-xs text-muted-foreground opacity-60 shrink-0">
                            {`{{${item.key}}}`}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="p-2 border-t bg-muted/30 text-xs text-muted-foreground flex gap-3">
          <span>↑↓ navegar</span>
          <span>↵ inserir</span>
          <span>esc fechar</span>
        </div>
      </div>
    );
  }
);

SlashCommandList.displayName = 'SlashCommandList';

export default SlashCommandList;
